/**
 * HOW OFTEN THE CLIENT RE-READS ITS BADGES, AND WHY THOSE TWO NUMBERS.
 *
 * Until 2026-09-21 there was one interval — 20 s, always, on every screen — and it carried far
 * more than badges: `GET /friends/all` + `GET /me/dm-summary` + `GET /me/inbox` were **81.6 % of
 * every api request prod served** (1,078,834 a day) and **550 MB a day** of response bodies.
 * It ran because most of what moves those three responses pushed nothing at all.
 *
 * Now everything that moves them publishes a `data-changed` push, so the interval is a SAFETY NET
 * rather than the delivery mechanism — with one exception that is not a safety net at all.
 */

/**
 * THE FALLBACK, while the presence socket is DOWN — unchanged from the original poll, deliberately.
 *
 * With no socket there are no pushes: this interval is not a net under the live path, it IS the
 * live path. Measured on prod over a full day, a client spends ~2.8 % of its app-open time in this
 * state, and the client cannot tell «my socket is down» from «the server has nothing to say», so
 * the only safe cadence here is the one that was there before.
 */
export const BADGE_POLL_FALLBACK_MS = 20_000;

/**
 * THE SAFETY NET, while the presence socket is UP. **120 s, and the number is derived, not chosen.**
 *
 * Three measurements bound it:
 *
 *  1. **CEILING — `LAST_SEEN_THROTTLE_MS` is 5 minutes.** Every authenticated request refreshes
 *     `User.lastSeenAt` if it is older than that, and nothing else refreshes it mid-session. A net
 *     slower than 5 min would let a long session drift outside one throttle period, which the
 *     hourly analytics rollup (`active7d`, `churnRisk`) and the panel's day buckets read. 120 s
 *     keeps every session inside one period with room to spare.
 *  2. **FLOOR — what the net is actually for.** It exists for a push that was published and
 *     reached nobody. Over the retained 9.1 h of the prod game-server log there were **zero**
 *     `resilient-subscribe` re-subscribe events, i.e. no observed window in which a publish could
 *     be lost while a socket was up; and the presence room now counts the events that reach no
 *     live socket (`[data-events] undelivered`), so the next tuning is measured rather than
 *     argued. A net for a rare event does not need to be fast.
 *  3. **SESSION LENGTH — median 12.7 min, p90 31.3 min** (prod, 2026-09-20, 989 single-socket
 *     sessions). At 120 s a median session still gets six net ticks; at 300 s the quarter of
 *     sessions under five minutes would get one or none.
 *
 * ⚠️ NOTHING PLAYER-VISIBLE DEPENDS ON THIS INTERVAL. Ban, the channel gate and maintenance used
 * to ride the 20 s tick — every request runs the auth hook — which would have made them 6× less
 * live. They are pushed now (`scope: "session"`), so they are immediate instead.
 */
export const BADGE_POLL_SAFETY_NET_MS = 120_000;

/**
 * HOW WIDE A **SYNCHRONISED** REFRESH IS SPREAD — and why there are two numbers, both derived.
 *
 * Almost every refresh in this design is one client answering one push about itself, and those
 * must stay instant: the measured write→pixel times are 31–162 ms and spreading them would be
 * the regression this batch exists to avoid. But THREE refreshes are synchronised across every
 * client at once, and nothing in the path spreads them at all:
 *
 *   ① a game-server recreate drops every presence socket, and each reconnect resyncs all THREE
 *      routes (`onReconnected`);
 *   ② `changedForAll("inbox")` — one in-app broadcast, one Colyseus `broadcast`, every client
 *      re-reads `/me/inbox`;
 *   ③ `changedForAll("session")` — a maintenance or channel-gate toggle, every client `GET /me`.
 *
 * ⚠️ FASTIFY IS ONE THREAD, AND A 304 DOES NOT MAKE A REQUEST CHEAPER. The ETag hook runs in
 * `onSend`, i.e. AFTER the handler has done every query, so a conditional re-read costs the
 * server the same as a full one and saves only bytes. N clients in one instant is therefore N
 * whole handlers in one instant.
 *
 * THE ARITHMETIC (prod's own p50s over a 3 h window, 2026-09-21: `/friends/all` 10.52 ms,
 * `/me/dm-summary` 9.89, `/me/inbox` 6.22, `/me` 9.12; N = 300, the peak concurrent presence
 * sockets derived in the journal). One second of one thread absorbs 1000/cost clients:
 *
 *   ① 26.63 ms per client ⇒ 37 clients/s ⇒ 300 clients need ≈8.1 s of span. The reconnect
 *      BACKOFF already supplies some — measured with the shipped `backoffDelayMs` in
 *      `reconnect-herd.test.ts` — but a FAST recreate is the WORST case, because most clients
 *      are still on their first or second (500 ms / 1 s) retry: at a 1.5 s outage **180 of 300
 *      land inside one second**, asking for 5.31 s of work. 5 s of extra spread brings that
 *      peak to ~36/s, i.e. just inside one thread.
 *   ②③ one route per client, ≤9.12 ms ⇒ ≥109 clients/s ⇒ ≈2.75 s of span, and here the backoff
 *      contributes NOTHING because nobody reconnected. 3 s.
 *
 * ⚠️ AND NEITHER IS «LESS LIVE». Both ceilings sit far inside the 20 s tick that carried all
 * three of these cases before the batch: a broadcast badge measured **3,479 ms** on the old
 * build with a true bound of (0, 20 s], against ≤3 s now; a post-reconnect resync had no
 * mechanism at all beyond that same tick.
 */
export const RECONNECT_RESYNC_SPREAD_MS = 5_000;
export const BROADCAST_RESYNC_SPREAD_MS = 3_000;
